import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { formatDateToDDMMYYYY } from '../utils/dateFormatter';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Calendar, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { PhoneInput } from './ui/phone-input';
import { DateInput } from './ui/date-input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get today's date in YYYY-MM-DD format
const getTodayYYYYMMDD = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Webhook helper function - fire and forget
const sendWebhook = async (type, data) => {
  try {
    await axios.post(`${API}/appointments/webhook/${type}`, data, { timeout: 5000 });
  } catch (error) {
    console.error(`Webhook ${type} failed (non-blocking):`, error.message);
  }
};

const DirectBooking = () => {
  const [step, setStep] = useState(1); // Step 1: Enter Hospital ID, Step 2: Book Appointment
  const [hospitalId, setHospitalId] = useState('');
  const [hospital, setHospital] = useState(null);
  const [validating, setValidating] = useState(false);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [useExistingBaby, setUseExistingBaby] = useState(true);
  const [foundBaby, setFoundBaby] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  // All possible time slots
  const allTimeSlots = [
    '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '12:00 PM', '12:30 PM', '01:00 PM', '01:30 PM', '02:00 PM', '02:30 PM',
    '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM', '05:00 PM', '05:30 PM',
    '06:00 PM', '06:30 PM', '07:00 PM', '07:30 PM', '08:00 PM', '08:30 PM'
  ];

  // Appointment form data
  const [appointmentData, setAppointmentData] = useState({
    baby_id: '',
    baby_name: '',
    baby_dob: '',
    baby_gender: 'Male',
    baby_phone: '',
    baby_country_code: '+91',
    doctor_id: '',
    appointment_date: getTodayYYYYMMDD(), // Default to today
    appointment_time: '',
    notes: '',
    appointment_type: 'new',  // new or followup
    followup_of_date: ''
  });
  const [lastVisitInfo, setLastVisitInfo] = useState(null);  // Info about last visit for free followup check

  // Auto-update appointment date after midnight
  useEffect(() => {
    const checkDateChange = () => {
      const currentDate = getTodayYYYYMMDD();
      
      // Only update if the appointment date is set to yesterday (after midnight)
      if (appointmentData.appointment_date && appointmentData.appointment_date < currentDate) {
        console.log(`🕐 DirectBooking: Date changed from ${appointmentData.appointment_date} to ${currentDate}`);
        setAppointmentData(prev => ({
          ...prev,
          appointment_date: currentDate,
          appointment_time: '' // Clear time as slots may have changed
        }));
        toast.info(`Appointment date updated to ${formatDateToDDMMYYYY(currentDate)}`);
      }
    };

    // Check every minute if date has changed
    const interval = setInterval(checkDateChange, 60000); // Check every 60 seconds
    
    // Also check immediately when component mounts or dialog opens
    checkDateChange();

    return () => clearInterval(interval);
  }, [appointmentData.appointment_date]);

  // Helper function to convert time string to minutes
  const timeToMinutes = (timeStr) => {
    const [time, period] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return hours * 60 + minutes;
  };

  // Get available time slots based on doctor schedule and leave dates
  const getAvailableTimeSlots = (doctorId, appointmentDate) => {
    if (!doctorId || !appointmentDate) return allTimeSlots;

    const selectedDoctor = doctors.find(d => d.id === doctorId);
    if (!selectedDoctor) return allTimeSlots;

    // Convert YYYY-MM-DD to DD/MM/YYYY for leave date checking
    const convertToDisplayDate = (dateStr) => {
      if (!dateStr) return '';
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    };

    const formattedDate = convertToDisplayDate(appointmentDate);

    // Check if appointment date is on doctor's leave
    if (selectedDoctor.leave_dates && selectedDoctor.leave_dates.length > 0) {
      const isOnLeave = selectedDoctor.leave_dates.includes(formattedDate);
      if (isOnLeave) {
        return []; // Return empty array if doctor is on leave
      }
    }

    // Parse doctor schedule (format: "09:00 AM - 05:00 PM")
    let doctorStartTime = '09:00 AM';
    let doctorEndTime = '05:00 PM';
    
    if (selectedDoctor.schedule) {
      const scheduleMatch = selectedDoctor.schedule.match(/(\d{1,2}:\d{2}\s*[AP]M)\s*-\s*(\d{1,2}:\d{2}\s*[AP]M)/i);
      if (scheduleMatch) {
        doctorStartTime = scheduleMatch[1].trim();
        doctorEndTime = scheduleMatch[2].trim();
      }
    }

    const startMinutes = timeToMinutes(doctorStartTime);
    const endMinutes = timeToMinutes(doctorEndTime);

    // Check if appointment date is today
    const today = getTodayYYYYMMDD(); // Use helper function for consistency
    const isToday = appointmentDate === today;
    
    // Get current time in minutes
    let currentTimeMinutes = 0;
    if (isToday) {
      const now = new Date();
      currentTimeMinutes = now.getHours() * 60 + now.getMinutes();
    }

    // Filter slots within doctor's working hours only
    // If today, also filter out past times
    return allTimeSlots.filter(slot => {
      const slotMinutes = timeToMinutes(slot);
      const withinWorkingHours = slotMinutes >= startMinutes && slotMinutes <= endMinutes;
      
      // If appointment is for today, check if time is in the future
      if (isToday) {
        return withinWorkingHours && slotMinutes > currentTimeMinutes;
      }
      
      return withinWorkingHours;
    });
  };

  // Handle DOB input with auto-advance
  const handleDobInput = (value) => {
    value = value.replace(/[^\d/]/g, '');
    
    if (value.length === 2 && !value.includes('/')) {
      value = value + '/';
    } else if (value.length === 5 && value.split('/').length === 2) {
      value = value + '/';
    }
    
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    setAppointmentData(prev => ({ ...prev, baby_dob: value }));
  };

  // Search for existing baby
  const handleSearchBaby = async () => {
    try {
      let baby = null;
      
      // Search by baby_id first
      if (appointmentData.baby_id) {
        const response = await axios.get(`${API}/babies/search`, {
          params: { query: appointmentData.baby_id }
        });
        if (response.data && response.data.length > 0) {
          baby = response.data[0];
        }
      }
      
      // If not found, search by name and DOB
      if (!baby && appointmentData.baby_name && appointmentData.baby_dob) {
        const response = await axios.get(`${API}/babies`);
        const babies = response.data || [];
        baby = babies.find(b => 
          b.name.toLowerCase() === appointmentData.baby_name.toLowerCase() &&
          b.date_of_birth === appointmentData.baby_dob
        );
      }
      
      if (baby) {
        setFoundBaby(baby);
        // Display short ID in the input field
        setAppointmentData(prev => ({ ...prev, baby_id: baby.short_id || baby.id.substring(0, 8).toUpperCase() }));
        toast.success(`Baby found: ${baby.name} (${baby.short_id || baby.id.substring(0, 8).toUpperCase()})`);
        // Fetch last visit info if doctor is selected
        if (appointmentData.doctor_id && hospital) {
          fetchLastVisitInfo(baby.id, appointmentData.doctor_id, hospital.id);
        }
      } else {
        setFoundBaby(null);
        setLastVisitInfo(null);
        toast.error('Baby not found. Please check the details.');
      }
    } catch (error) {
      console.error('Error searching baby:', error);
      toast.error('Failed to search baby');
    }
  };

  // Fetch last visit info for a baby with a doctor to check for free follow-up
  const fetchLastVisitInfo = async (babyId, doctorId, hospitalId) => {
    try {
      const response = await axios.get(`${API}/appointments/last-visit/${babyId}/${doctorId}/${hospitalId}`);
      setLastVisitInfo(response.data);
    } catch (error) {
      console.error('Error fetching last visit info:', error);
      setLastVisitInfo(null);
    }
  };

  // Validate hospital ID (using hospital code)
  const validateHospitalId = async () => {
    if (!hospitalId.trim()) {
      toast.error('Please enter a Hospital Code');
      return;
    }

    setValidating(true);
    try {
      // Try to get hospital by code first
      const response = await axios.get(`${API}/hospitals/by-code/${hospitalId.toUpperCase()}`);
      if (response.data) {
        setHospital(response.data);
        setStep(2);
        toast.success(`Hospital found: ${response.data.name}`);
        // Load doctors for this hospital
        const doctorsResponse = await axios.get(`${API}/doctors`, {
          params: { hospital_id: response.data.id }
        });
        setDoctors(doctorsResponse.data || []);
      }
    } catch (error) {
      console.error('Error validating hospital:', error);
      if (error.response?.status === 404) {
        toast.error('Hospital Code not found. Please check and try again.');
      } else {
        toast.error('Error validating hospital. Please try again.');
      }
    } finally {
      setValidating(false);
    }
  };

  // Handle appointment booking
  const handleBookAppointment = async (e) => {
    e.preventDefault();
    
    // Validation for existing baby
    if (useExistingBaby) {
      if (!foundBaby) {
        toast.error('Please search and select an existing baby first');
        return;
      }
      if (!appointmentData.baby_phone) {
        toast.error('Please enter phone number for appointment');
        return;
      }
    } else {
      // Validation for new baby
      if (!appointmentData.baby_name || !appointmentData.baby_dob || !appointmentData.baby_phone) {
        toast.error('Please fill in all baby details');
        return;
      }
      
      // Validate DOB format (DD/MM/YYYY)
      const dobPattern = /^\d{2}\/\d{2}\/\d{4}$/;
      if (!dobPattern.test(appointmentData.baby_dob)) {
        toast.error('Please enter a valid date of birth in DD/MM/YYYY format');
        return;
      }
      
      // Validate DOB is a real date
      const [day, month, year] = appointmentData.baby_dob.split('/').map(Number);
      const dobDate = new Date(year, month - 1, day);
      if (dobDate.getDate() !== day || dobDate.getMonth() !== month - 1 || dobDate.getFullYear() !== year) {
        toast.error('Please enter a valid date of birth');
        return;
      }
    }

    // Common validation
    if (!appointmentData.doctor_id || !appointmentData.appointment_date || !appointmentData.appointment_time) {
      toast.error('Please fill in doctor, date and time');
      return;
    }

    setSubmitting(true);
    try {
      const appointmentPayload = {
        baby_id: useExistingBaby ? foundBaby.id : null,
        baby_name: useExistingBaby ? foundBaby.name : appointmentData.baby_name,
        baby_dob: useExistingBaby ? foundBaby.date_of_birth : appointmentData.baby_dob,
        baby_gender: useExistingBaby ? foundBaby.gender : appointmentData.baby_gender,
        baby_phone: appointmentData.baby_phone,
        parent_phone: appointmentData.baby_phone,
        doctor_id: appointmentData.doctor_id,
        hospital_id: hospital.id,
        hospital_name: hospital.name,
        appointment_date: appointmentData.appointment_date, // Keep in YYYY-MM-DD format for consistency
        appointment_time: appointmentData.appointment_time,
        notes: appointmentData.notes,
        status: 'pending',
        booking_source: 'direct_booking',
        created_at: new Date().toISOString(),
        appointment_type: useExistingBaby ? appointmentData.appointment_type : 'new',  // New babies are always new visits
        followup_of_date: appointmentData.appointment_type === 'followup' ? (lastVisitInfo?.last_visit_date || appointmentData.followup_of_date) : null
      };

      const response = await axios.post(`${API}/appointments`, appointmentPayload);
      
      // Send webhook notification (non-blocking)
      sendWebhook('created', response.data);
      
      // Prepare receipt data
      const selectedDoctor = doctors.find(d => d.id === appointmentData.doctor_id);
      const babyShortId = useExistingBaby 
        ? (foundBaby.short_id || foundBaby.id.substring(0, 8).toUpperCase())
        : 'Will be generated';
      
      // Get doctor-specific consultation fee (0 if free follow-up visit)
      let doctorFee = selectedDoctor 
        ? (hospital.doctor_fees?.[selectedDoctor.id] || hospital.consultation_fee || 0)
        : 0;
      
      // If it's a free follow-up visit, set fee to 0
      if (response.data.is_free_visit) {
        doctorFee = 0;
      }
      
      setReceiptData({
        babyName: useExistingBaby ? foundBaby.name : appointmentData.baby_name,
        babyId: babyShortId,
        bookingDate: formatDateToDDMMYYYY(new Date()),
        appointmentDate: formatDateToDDMMYYYY(appointmentData.appointment_date),
        appointmentTime: appointmentData.appointment_time,
        clinicName: hospital.name,
        clinicLogo: hospital.logo_url || null,
        clinicAddress: hospital.address || hospital.address_line1 || null,
        doctorName: selectedDoctor ? `Dr. ${selectedDoctor.name}` : 'To be assigned',
        doctorSpecialization: selectedDoctor ? selectedDoctor.specialization : '',
        consultationFee: doctorFee,
        isFreeVisit: response.data.is_free_visit,
        appointmentType: response.data.appointment_type
      });
      
      toast.success('Appointment booked successfully!');
      setShowBookingDialog(false);
      setShowReceipt(true);
      
      // Reset form
      setAppointmentData({
        baby_id: '',
        baby_name: '',
        baby_dob: '',
        baby_gender: 'Male',
        baby_phone: '',
        baby_country_code: '+91',
        doctor_id: '',
        appointment_date: getTodayYYYYMMDD(), // Reset to today using helper
        appointment_time: '',
        notes: ''
      });
      setFoundBaby(null);
      setUseExistingBaby(false);
      
    } catch (error) {
      console.error('Error booking appointment:', error);
      toast.error('Failed to book appointment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                Direct Appointment Booking
              </h1>
              <p className="text-sm text-gray-600">Book your appointment quickly and easily</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          
          {/* Step 1: Enter Hospital ID */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                  <FileText className="w-8 h-8 text-purple-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Enter Hospital Code</h2>
                <p className="text-gray-600">Please enter your hospital code to proceed with booking</p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="hospitalId" className="text-sm font-medium text-gray-700">
                    Hospital Code *
                  </Label>
                  <Input
                    id="hospitalId"
                    type="text"
                    placeholder="Enter hospital code (e.g., TSHYD167)"
                    value={hospitalId}
                    onChange={(e) => setHospitalId(e.target.value.toUpperCase())}
                    onKeyPress={(e) => e.key === 'Enter' && validateHospitalId()}
                    className="mt-1"
                  />
                </div>

                <Button 
                  onClick={validateHospitalId}
                  disabled={validating}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                >
                  {validating ? 'Validating...' : 'Continue'}
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Book Appointment Button */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <Calendar className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Hospital Verified</h2>
                <div className="bg-purple-50 rounded-lg p-4 mb-4">
                  <p className="text-lg font-semibold text-purple-900">{hospital.name}</p>
                  {hospital.address && (
                    <p className="text-sm text-purple-700 mt-1">{hospital.address}</p>
                  )}
                </div>
                <p className="text-gray-600">Click the button below to book your appointment</p>
              </div>

              <Button 
                onClick={() => setShowBookingDialog(true)}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-lg py-6"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Appointment
              </Button>

              <Button 
                variant="outline"
                onClick={() => {
                  setStep(1);
                  setHospital(null);
                  setHospitalId('');
                }}
                className="w-full"
              >
                Change Hospital
              </Button>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Contact Hospital after booking to reconfirm your appointment details.
          </p>
        </div>
      </div>

      {/* Booking Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
        <DialogContent className="sm:max-w-md z-50 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Book Appointment</DialogTitle>
            <DialogDescription>
              Fill in the details below to book an appointment at {hospital?.name || 'the hospital'}.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleBookAppointment} className="space-y-4">
            {/* Toggle between Existing and New baby */}
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  checked={useExistingBaby}
                  onChange={() => setUseExistingBaby(true)}
                  className="w-4 h-4"
                />
                <span className="text-sm">Existing</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  checked={!useExistingBaby}
                  onChange={() => setUseExistingBaby(false)}
                  className="w-4 h-4"
                />
                <span className="text-sm">New</span>
              </label>
            </div>

            {useExistingBaby ? (
              <>
                {/* Existing Baby Fields */}
                <div>
                  <Label htmlFor="baby_id">ID</Label>
                  <Input
                    id="baby_id"
                    value={appointmentData.baby_id}
                    onChange={(e) => {
                      setAppointmentData(prev => ({ ...prev, baby_id: e.target.value.toUpperCase() }));
                      setFoundBaby(null);
                    }}
                    placeholder="Enter ID"
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
                
                <div className="text-center text-gray-500 text-sm">OR</div>
                
                <div>
                  <Label htmlFor="search_baby_name">Name</Label>
                  <Input
                    id="search_baby_name"
                    value={appointmentData.baby_name}
                    onChange={(e) => {
                      const value = e.target.value;
                      const capitalized = value.charAt(0).toUpperCase() + value.slice(1);
                      setAppointmentData(prev => ({ ...prev, baby_name: capitalized }));
                      setFoundBaby(null);
                    }}
                    placeholder="Enter Name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="search_baby_dob">Date of Birth (DD/MM/YYYY)</Label>
                  <Input
                    id="search_baby_dob"
                    value={appointmentData.baby_dob}
                    onChange={(e) => handleDobInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' || e.key === 'Delete') {
                        const value = appointmentData.baby_dob;
                        if (value.endsWith('/')) {
                          e.preventDefault();
                          setAppointmentData(prev => ({ ...prev, baby_dob: value.slice(0, -1) }));
                        }
                      }
                    }}
                    placeholder="DD/MM/YYYY"
                    maxLength={10}
                  />
                </div>
                
                <Button
                  type="button"
                  onClick={handleSearchBaby}
                  variant="outline"
                  className="w-full"
                >
                  Search
                </Button>
                
                {foundBaby && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm font-semibold text-green-700">✓ Found</p>
                    <p className="text-sm text-green-800">{foundBaby.name}</p>
                    <p className="text-xs text-green-600">ID: <span className="uppercase font-mono">{foundBaby.short_id || foundBaby.id.substring(0, 8).toUpperCase()}</span></p>
                    <p className="text-xs text-green-600">DOB: {foundBaby.date_of_birth}</p>
                  </div>
                )}
                
                <div>
                  <Label htmlFor="existing_baby_phone">Phone Number for Appointment *</Label>
                  <PhoneInput
                    value={appointmentData.baby_phone}
                    onChange={(value) => setAppointmentData(prev => ({ ...prev, baby_phone: value }))}
                    countryCode={appointmentData.baby_country_code}
                    onCountryCodeChange={(code) => setAppointmentData(prev => ({ ...prev, baby_country_code: code }))}
                    placeholder="Enter 10-digit mobile number"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Phone number is for appointment only</p>
                </div>
                
                {/* Appointment Type Selection - Only for existing patients */}
                {foundBaby && (
                  <div>
                    <Label>Appointment Type *</Label>
                    <div className="flex gap-6 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="appointment_type"
                          value="new"
                          checked={appointmentData.appointment_type === 'new'}
                          onChange={(e) => setAppointmentData(prev => ({...prev, appointment_type: e.target.value, followup_of_date: ''}))}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm font-medium text-gray-700">New Visit</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="appointment_type"
                          value="followup"
                          checked={appointmentData.appointment_type === 'followup'}
                          onChange={(e) => setAppointmentData(prev => ({...prev, appointment_type: e.target.value}))}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm font-medium text-gray-700">Follow-up</span>
                      </label>
                    </div>
                    {/* Show free visit info if within 7 days */}
                    {appointmentData.appointment_type === 'followup' && lastVisitInfo?.is_within_7_days && (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-xs text-green-700 font-medium">
                          ✅ Free Follow-up Visit (within 7 days of last visit on {lastVisitInfo.last_visit_date})
                        </p>
                        <p className="text-xs text-green-600">No consultation fee will be charged</p>
                      </div>
                    )}
                    {appointmentData.appointment_type === 'followup' && lastVisitInfo && !lastVisitInfo.is_within_7_days && lastVisitInfo.has_previous_visit && (
                      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-xs text-yellow-700">
                          Last visit was {lastVisitInfo.days_since_last_visit} days ago on {lastVisitInfo.last_visit_date}
                        </p>
                        <p className="text-xs text-yellow-600">Normal consultation fee applies</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                {/* New Baby Fields */}
                <div>
                  <Label htmlFor="baby_name">Name *</Label>
                  <Input
                    id="baby_name"
                    value={appointmentData.baby_name}
                    onChange={(e) => {
                      const value = e.target.value;
                      const capitalized = value.charAt(0).toUpperCase() + value.slice(1);
                      setAppointmentData(prev => ({ ...prev, baby_name: capitalized }));
                    }}
                    placeholder="e.g., Aarav"
                  />
                </div>
                
                <div>
                  <Label htmlFor="baby_dob">Date of Birth (DD/MM/YYYY) *</Label>
                  <Input
                    id="baby_dob"
                    value={appointmentData.baby_dob}
                    onChange={(e) => handleDobInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' || e.key === 'Delete') {
                        const value = appointmentData.baby_dob;
                        if (value.endsWith('/')) {
                          e.preventDefault();
                          setAppointmentData(prev => ({ ...prev, baby_dob: value.slice(0, -1) }));
                        }
                      }
                    }}
                    placeholder="DD/MM/YYYY"
                    maxLength={10}
                  />
                </div>
                
                <div>
                  <Label htmlFor="baby_gender">Gender *</Label>
                  <div className="grid grid-cols-2 gap-3 mt-1">
                    <button
                      type="button"
                      onClick={() => setAppointmentData(prev => ({ ...prev, baby_gender: 'Male' }))}
                      className={`h-12 rounded-lg border-2 font-semibold transition-all ${
                        appointmentData.baby_gender === 'Male'
                          ? 'bg-blue-500 text-white border-blue-600 shadow-lg'
                          : 'bg-white text-blue-600 border-blue-300 hover:bg-blue-50'
                      }`}
                    >
                      👦 Male
                    </button>
                    <button
                      type="button"
                      onClick={() => setAppointmentData(prev => ({ ...prev, baby_gender: 'Female' }))}
                      className={`h-12 rounded-lg border-2 font-semibold transition-all ${
                        appointmentData.baby_gender === 'Female'
                          ? 'bg-pink-500 text-white border-pink-600 shadow-lg'
                          : 'bg-white text-pink-600 border-pink-300 hover:bg-pink-50'
                      }`}
                    >
                      👧 Female
                    </button>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="baby_phone">Phone Number for Appointment *</Label>
                  <PhoneInput
                    value={appointmentData.baby_phone}
                    onChange={(value) => setAppointmentData(prev => ({ ...prev, baby_phone: value }))}
                    countryCode={appointmentData.baby_country_code}
                    onCountryCodeChange={(code) => setAppointmentData(prev => ({ ...prev, baby_country_code: code }))}
                    placeholder="Enter 10-digit mobile number"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Phone number is for appointment only, not saved to baby profile</p>
                </div>
              </>
            )}

            {/* Doctor Selection */}
            <div>
              <Label htmlFor="doctor_id">Select Doctor *</Label>
              <select
                id="doctor_id"
                value={appointmentData.doctor_id}
                onChange={(e) => {
                  const newDoctorId = e.target.value;
                  setAppointmentData(prev => ({ ...prev, doctor_id: newDoctorId }));
                  // Fetch last visit info when doctor is selected and baby is found
                  if (foundBaby && newDoctorId && hospital) {
                    fetchLastVisitInfo(foundBaby.id, newDoctorId, hospital.id);
                  }
                }}
                className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
              >
                <option value="">Select Doctor</option>
                {doctors
                  .filter(doctor => {
                    // Filter out doctors who are on leave for the selected date
                    if (!appointmentData.appointment_date) return true;
                    if (!doctor.leave_dates || doctor.leave_dates.length === 0) return true;
                    
                    const selectedDate = appointmentData.appointment_date; // YYYY-MM-DD format
                    
                    // Check if doctor is on leave - support both date formats
                    const isOnLeave = doctor.leave_dates.some(leaveDate => {
                      // If leave date is in DD/MM/YYYY format, convert to YYYY-MM-DD
                      if (leaveDate.includes('/')) {
                        const [day, month, year] = leaveDate.split('/');
                        const normalizedLeaveDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                        return normalizedLeaveDate === selectedDate;
                      }
                      // If already in YYYY-MM-DD format, compare directly
                      return leaveDate === selectedDate;
                    });
                    
                    return !isOnLeave; // Only show if NOT on leave
                  })
                  .map(doctor => (
                    <option key={doctor.id} value={doctor.id}>
                      {doctor.name} - {doctor.specialization}
                    </option>
                  ))
                }
              </select>
              {appointmentData.appointment_date && doctors.filter(d => {
                if (!d.leave_dates) return false;
                const [year, month, day] = appointmentData.appointment_date.split('-');
                const formattedDate = `${day}/${month}/${year}`;
                return d.leave_dates.includes(formattedDate);
              }).length > 0 && (
                <p className="text-xs text-orange-600 mt-1">
                  ⚠️ Some doctors are on leave on this date
                </p>
              )}
            </div>

            {/* Appointment Date */}
            <div>
              <Label htmlFor="appointment_date">Appointment Date *</Label>
              <div className="relative">
                <DateInput
                  id="appointment_date"
                  value={appointmentData.appointment_date}
                  onChange={(e) => {
                    setAppointmentData(prev => ({ ...prev, appointment_date: e.target.value }));
                    e.target.blur();
                  }}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm opacity-0 absolute inset-0 z-10 cursor-pointer"
                />
                {/* Custom date display for consistent DD/MM/YYYY format */}
                <div className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm flex items-center justify-between cursor-pointer">
                  <span className={appointmentData.appointment_date ? 'text-gray-900' : 'text-gray-400'}>
                    {appointmentData.appointment_date ? formatDateToDDMMYYYY(appointmentData.appointment_date) : 'Select date'}
                  </span>
                  <Calendar className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </div>

            {/* Appointment Time */}
            <div>
              <Label htmlFor="appointment_time">Appointment Time *</Label>
              <select
                id="appointment_time"
                value={appointmentData.appointment_time}
                onChange={(e) => setAppointmentData(prev => ({ ...prev, appointment_time: e.target.value }))}
                className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
                disabled={!appointmentData.doctor_id || !appointmentData.appointment_date}
              >
                <option value="">
                  {!appointmentData.doctor_id ? 'Select doctor first' : 
                   !appointmentData.appointment_date ? 'Select date first' : 
                   'Select Time'}
                </option>
                {getAvailableTimeSlots(appointmentData.doctor_id, appointmentData.appointment_date).map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
              {appointmentData.doctor_id && appointmentData.appointment_date && 
               getAvailableTimeSlots(appointmentData.doctor_id, appointmentData.appointment_date).length === 0 && (() => {
                 const selectedDoctor = doctors.find(d => d.id === appointmentData.doctor_id);
                 const selectedDate = appointmentData.appointment_date; // YYYY-MM-DD
                 
                 // Check if doctor is on leave - support both date formats
                 const isOnLeave = selectedDoctor?.leave_dates?.some(leaveDate => {
                   if (leaveDate.includes('/')) {
                     const [day, month, year] = leaveDate.split('/');
                     const normalizedLeaveDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                     return normalizedLeaveDate === selectedDate;
                   }
                   return leaveDate === selectedDate;
                 });
                 
                 return (
                   <p className="text-xs text-red-500 mt-1 font-medium">
                     {isOnLeave ? '⚠️ Doctor is on leave on this date' : 'Doctor not available on selected date'}
                   </p>
                 );
               })()}
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Notes</Label>
              <textarea
                id="notes"
                value={appointmentData.notes}
                onChange={(e) => setAppointmentData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex space-x-2">
              <Button 
                type="submit" 
                disabled={submitting}
                className="flex-1"
              >
                {submitting ? 'Booking...' : 'Book Appointment'}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowBookingDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader className="no-print">
            <DialogTitle className="text-center text-lg font-bold text-green-600">
              ✅ Booking Confirmed!
            </DialogTitle>
          </DialogHeader>
          
          {receiptData && (
            <>
              {/* Important Notice - Hidden in Print */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 no-print">
                <p className="text-xs text-yellow-800 text-center font-medium">
                  📌 Save this receipt. You will be contacted by the clinic soon.
                </p>
              </div>

              {/* Receipt Content - Screen Version */}
              <div className="border border-gray-200 rounded-lg p-4">
                {/* Hospital Branding */}
                <div style={{ textAlign: 'center', paddingBottom: '12px', borderBottom: '1px dashed #d1d5db', marginBottom: '12px' }}>
                  {receiptData.clinicLogo && (
                    <img 
                      src={receiptData.clinicLogo.startsWith('http') ? receiptData.clinicLogo : `${BACKEND_URL}${receiptData.clinicLogo}`}
                      alt="Hospital logo" 
                      style={{ height: '48px', width: '48px', objectFit: 'contain', margin: '0 auto 8px auto', display: 'block' }}
                      crossOrigin="anonymous"
                    />
                  )}
                  <h3 style={{ fontSize: '16px', fontWeight: 'bold', margin: '0', color: '#111827' }}>{receiptData.clinicName}</h3>
                  {receiptData.clinicAddress && (
                    <p style={{ fontSize: '11px', color: '#4b5563', margin: '4px 0 0 0' }}>{receiptData.clinicAddress}</p>
                  )}
                  <p style={{ fontSize: '14px', color: '#374151', marginTop: '8px', fontWeight: '600' }}>Appointment Receipt</p>
                </div>

                {/* Receipt Details */}
                <div style={{ fontSize: '13px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span style={{ color: '#4b5563' }}>Name:</span>
                    <span style={{ fontWeight: '600' }}>{receiptData.babyName}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span style={{ color: '#4b5563' }}>ID:</span>
                    <span style={{ fontWeight: '600' }}>{receiptData.babyId}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span style={{ color: '#4b5563' }}>Booking Date:</span>
                    <span style={{ fontWeight: '600' }}>{receiptData.bookingDate}</span>
                  </div>
                  <div style={{ height: '1px', backgroundColor: '#d1d5db', margin: '8px 0' }}></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span style={{ color: '#4b5563' }}>Appointment Date:</span>
                    <span style={{ fontWeight: 'bold', color: '#16a34a' }}>{receiptData.appointmentDate}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span style={{ color: '#4b5563' }}>Appointment Time:</span>
                    <span style={{ fontWeight: 'bold', color: '#16a34a' }}>{receiptData.appointmentTime}</span>
                  </div>
                  {/* Visit Type - Show for follow-up appointments */}
                  {receiptData.appointmentType && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                      <span style={{ color: '#4b5563' }}>Visit Type:</span>
                      <span style={{ fontWeight: '600', color: receiptData.isFreeVisit ? '#16a34a' : '#374151' }}>
                        {receiptData.appointmentType === 'followup' 
                          ? (receiptData.isFreeVisit ? 'Follow-Up (Free)' : 'Follow-Up')
                          : 'New Visit'}
                      </span>
                    </div>
                  )}
                  <div style={{ height: '1px', backgroundColor: '#d1d5db', margin: '8px 0' }}></div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span style={{ color: '#4b5563' }}>Doctor:</span>
                    <span style={{ fontWeight: '600' }}>{receiptData.doctorName}</span>
                  </div>
                  {receiptData.doctorSpecialization && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                      <span style={{ color: '#4b5563' }}>Specialization:</span>
                      <span style={{ color: '#374151' }}>{receiptData.doctorSpecialization}</span>
                    </div>
                  )}
                </div>
                
                {/* Fee Section */}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', fontWeight: 'bold', paddingTop: '12px', marginTop: '8px', borderTop: '1px solid #d1d5db' }}>
                  <span style={{ color: '#111827' }}>Consultation Fee:</span>
                  <span style={{ color: '#16a34a' }}>
                    {receiptData.consultationFee > 0 ? `₹${receiptData.consultationFee}` : 'To be confirmed'}
                  </span>
                </div>

                {/* Footer */}
                <div style={{ textAlign: 'center', fontSize: '11px', color: '#6b7280', fontStyle: 'italic', marginTop: '16px', paddingTop: '12px', borderTop: '1px dashed #d1d5db' }}>
                  <p style={{ margin: '0' }}>Please arrive 10 minutes before your appointment</p>
                  <p style={{ margin: '4px 0 0 0' }}>Thank you for choosing {receiptData.clinicName}</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-4 no-print">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowReceipt(false);
                    setStep(1);
                    setHospital(null);
                    setHospitalId('');
                  }}
                  className="flex-1"
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    // Create a new window for printing
                    const printWindow = window.open('', '_blank');
                    const receiptHTML = `
                      <!DOCTYPE html>
                      <html>
                      <head>
                        <title>Appointment Receipt</title>
                        <style>
                          body { font-family: Arial, sans-serif; padding: 20px; margin: 0; }
                          .receipt { max-width: 400px; margin: 0 auto; }
                          .header { text-align: center; padding-bottom: 15px; border-bottom: 1px dashed #ccc; margin-bottom: 15px; }
                          .header img { height: 50px; margin-bottom: 10px; }
                          .header h2 { margin: 0; font-size: 18px; }
                          .header p { margin: 5px 0; color: #666; font-size: 12px; }
                          .title { font-size: 14px; font-weight: 600; margin-top: 10px; }
                          .row { display: flex; justify-content: space-between; padding: 5px 0; font-size: 13px; }
                          .row .label { color: #666; }
                          .row .value { font-weight: 500; }
                          .divider { height: 1px; background: #ddd; margin: 10px 0; }
                          .fee-row { display: flex; justify-content: space-between; padding: 15px 0; font-size: 15px; font-weight: bold; border-top: 1px solid #ddd; margin-top: 10px; }
                          .footer { text-align: center; font-size: 11px; color: #888; font-style: italic; margin-top: 20px; padding-top: 15px; border-top: 1px dashed #ccc; }
                          .green { color: #16a34a; }
                          @media print { body { padding: 0; } }
                        </style>
                      </head>
                      <body>
                        <div class="receipt">
                          <div class="header">
                            ${receiptData.clinicLogo ? `<img src="${receiptData.clinicLogo.startsWith('http') ? receiptData.clinicLogo : BACKEND_URL + receiptData.clinicLogo}" alt="Logo" crossorigin="anonymous" />` : ''}
                            <h2>${receiptData.clinicName || ''}</h2>
                            ${receiptData.clinicAddress ? `<p>${receiptData.clinicAddress}</p>` : ''}
                            <p class="title">Appointment Receipt</p>
                          </div>
                          <div class="row"><span class="label">Name:</span><span class="value">${receiptData.babyName || ''}</span></div>
                          <div class="row"><span class="label">ID:</span><span class="value">${receiptData.babyId || ''}</span></div>
                          <div class="row"><span class="label">Booking Date:</span><span class="value">${receiptData.bookingDate || ''}</span></div>
                          <div class="divider"></div>
                          <div class="row"><span class="label">Appointment Date:</span><span class="value green">${receiptData.appointmentDate || ''}</span></div>
                          <div class="row"><span class="label">Appointment Time:</span><span class="value green">${receiptData.appointmentTime || ''}</span></div>
                          ${receiptData.appointmentType ? `<div class="row"><span class="label">Visit Type:</span><span class="value ${receiptData.isFreeVisit ? 'green' : ''}">${receiptData.appointmentType === 'followup' ? (receiptData.isFreeVisit ? 'Follow-Up (Free)' : 'Follow-Up') : 'New Visit'}</span></div>` : ''}
                          <div class="divider"></div>
                          <div class="row"><span class="label">Doctor:</span><span class="value">${receiptData.doctorName || ''}</span></div>
                          ${receiptData.doctorSpecialization ? `<div class="row"><span class="label">Specialization:</span><span class="value">${receiptData.doctorSpecialization}</span></div>` : ''}
                          <div class="fee-row">
                            <span>Consultation Fee:</span>
                            <span class="green">${receiptData.consultationFee > 0 ? '₹' + receiptData.consultationFee : 'To be confirmed'}</span>
                          </div>
                          <div class="footer">
                            <p>Please arrive 10 minutes before your appointment</p>
                            <p>Thank you for choosing ${receiptData.clinicName || 'us'}</p>
                          </div>
                        </div>
                        <script>window.onload = function() { window.print(); }</script>
                      </body>
                      </html>
                    `;
                    printWindow.document.write(receiptHTML);
                    printWindow.document.close();
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  Print
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DirectBooking;
